import {
  FRESH_PRODUCT_TYPES,
  PRODUCT_TYPES,
  STATE,
  ONBOARDING_VARIATIONS,
  EXTRAS_VARIATIONS,
} from './constants';

export const hasCustomer = (ctx, { payload }) => {
  return !!ctx.customer?.first_name || !!payload.customer?.first_name;
};

export const initialPetComplete = (ctx, { payload }) => {
  const pet = payload.pets.find(p => p.id === payload.pet_id);
  return !!pet?.name && !!pet?.weight && !!pet?.activity_level;
};

export const hasNoCustomer = (ctx, { payload }) => {
  return !ctx.customer?.first_name || !payload.customer?.first_name;
};

export const hasActiveCustomer = (ctx, { payload }) => {
  return (
    payload.orderId ||
    payload.isActive ||
    payload.payload?.statusText === 'Created' ||
    payload.customer?.status === 'active' ||
    ctx.customer?.status === 'active'
  );
};

export const hasPet = (ctx, { payload }) => {
  return !!ctx.pet.id || !!payload.pet?.id || !!payload.pets?.[0]?.id;
};

export const hasPetName = (ctx, action) => {
  return !!ctx?.pet?.name;
};

export const hasInitialPetWeight = (ctx, action) => {
  return !!ctx?.pet?.weight;
};

export const hasIncompletePetWithName = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.name;
  }
};

export const hasIncompletePetWithBirthday = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.birth_date;
  }
};

export const hasPetBirthday = (ctx, { payload }) => {
  return !!ctx.pet?.birth_date || !!payload.pet?.birth_date;
};

export const hasIncompletePetWithBreed = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet?.breeds?.length;
  }
};

export const hasPetBreed = (ctx, { payload }) => {
  return !!ctx.pet?.breeds?.length || !!payload.pet?.breeds?.length;
};

export const hasIncompletePetWithWeight = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.weight;
  }
};

export const hasPetWeight = (ctx, { payload }) => {
  return !!ctx.pet?.weight || !!payload.pet?.weight;
};

export const hasIncompletePetWithActivity = (ctx, { payload }) => {
  if (!payload.pets.length) return false;
  else {
    const pet = payload.pets.find(pet => !isCompletePet(pet));
    return !!pet.activity_level;
  }
};

export const notGoBack = (ctx, action) => {
  return !(action.type === 'goBack');
};

export const inInitialFlow = (ctx, action) => {
  return !!ctx.initialFlow;
};

export const isInitialFlow = (ctx, action) => {
  return action.type === 'initialLoad' || ctx.initialFlow;
};

export const hasInitialPetActivity = (ctx, action) => {
  return !!ctx.pet?.activity_level;
};

export const hasPetActivity = (ctx, { payload }) => {
  return !!ctx.pet?.activity_level || !!payload.pet?.activity_level;
};

export const isCompletePet = pet => {
  return !!pet.name && !!pet.weight && !!pet.activity_level;
};

export const hasCompletePet = (ctx, { payload }) => {
  return (
    !!isCompletePet(payload.pet) ||
    !!isCompletePet(ctx.pet) ||
    !!payload.pets?.find(pet => isCompletePet(pet)) ||
    !!ctx.pets?.find(pet => isCompletePet(pet))
  );
};

export const allPetsComplete = (ctx, { payload }) => {
  return (
    (!!payload.pets.length && payload.pets.every(pet => isCompletePet(pet))) ||
    (!!ctx.pets?.length && ctx.pets.every(pet => isCompletePet(pet)))
  );
};

export const getFirstPetWithNoProductInCart = (ctx, { payload }) =>
  payload.pets.find(
    pet => !payload.cart.line_items.find(item => item.pet_id === pet.id),
  );

export const isAddPup = (ctx, { payload }) => !!payload.isAddPet;
export const isEditPet = (ctx, { payload }) => !!payload.isEditPet;
// Uses context since it's called after initial load and already integrated into context
export const notEditPet = (ctx, { payload }) => !ctx.isEditPet;

export const notAddPup = (ctx, { payload }) => !payload.isAddPet;

export const isDeletePet = (ctx, { payload }) => {
  return !!payload.isDeletePet;
};

export const notDeletePet = (ctx, { payload }) => !payload.isDeletePet;

export const allPetsHaveProductInCart = (ctx, { payload }) => {
  return (
    !!payload.pets?.length && payload.pets?.every(pet => !!pet.starter_box)
  );
};

export const selectedDry = (ctx, { payload }) =>
  payload.planType === PRODUCT_TYPES.dry;

export const hasRecipes = ctx => {
  switch (ctx.mealPrepType) {
    case PRODUCT_TYPES.fresh:
    case PRODUCT_TYPES.half_fresh:
      return !!ctx.recipes.fresh.length;
    case PRODUCT_TYPES.dry:
      return !!ctx.recipes.dry.length;
    case PRODUCT_TYPES.mixed:
      return !!ctx.recipes.fresh.length && !!ctx.recipes.mixed.length;
    default:
      return false;
  }
};

export const hasFreshInCart = (ctx, { payload: { recipes } }) =>
  !!recipes.fresh?.length;
export const hasDryInCart = (ctx, { payload: { recipes } }) =>
  !!recipes.dry?.length;
export const hasAddonsInCart = (ctx, { payload: { addons } }) =>
  !!addons.length;

export const hasMealPrep = ctx => !!ctx.mealPrepType;
export const isFresh = ctx => FRESH_PRODUCT_TYPES.includes(ctx.planType);
export const isBaked = ctx => ctx.planType === PRODUCT_TYPES.dry;
export const isMixed = ctx => ctx.planType === PRODUCT_TYPES.mixed;
export const isBakedOrMixed = ctx => isBaked(ctx) || isMixed(ctx);
export const isFreshOrMixed = ctx => isFresh(ctx) || isMixed(ctx);
export const isFreshUrl = (ctx, { payload }) =>
  payload.pathname === STATE.fresh;
export const isBakedUrl = (ctx, { payload }) =>
  payload.pathname === STATE.baked;
export const isAddonsUrl = (ctx, { payload }) =>
  payload.pathname === STATE.addons;
export const prevStateBaked = ctx => ctx.prevState.stateKey === STATE.baked;
export const prevStateFresh = ctx => ctx.prevState.stateKey === STATE.fresh;
export const prevStateAddons = ctx => ctx.prevState.stateKey === STATE.addons;
export const submitAddonToCartFailed = (ctx, payload) => !payload.data.success;
export const submitCartFailed = (ctx, payload) => !payload.data.success;

//This function is correct now, don't destructure the payload here.
//This comment can be deleted once ES-1199 in completed- Dave
export const showRecommendedPlan = (ctx, action) => {
  return true;
};
export const skipRecommendedPlan = (ctx, payload) =>
  !showRecommendedPlan(ctx, payload);

export const showExtrasPage = (ctx, payload) => {
  return !(ctx.flags?.extrasPageCRO === EXTRAS_VARIATIONS.noPage);
};

export const noPersonalizedPlan = (ctx, payload) => {
  return !ctx.personalizedPlan ?? !payload.personalizedPlan;
};

export const personalizedPlan = (ctx, payload) => {
  return ctx.personalizedPlan ?? payload.personalizedPlan;
};

export const isAppSession = (ctx, { payload }) => {
  return ctx.isAppSession || payload?.isAppSession;
};

export const showAllergiesQuestion = (ctx, action) => {
  return (
    ctx.flags?.shortenedOnboardingQuiz ===
      ONBOARDING_VARIATIONS.requiredWithAllergies ??
    action?.payload.flags?.shortenedOnboardingQuiz ===
      ONBOARDING_VARIATIONS.requiredWithAllergies
  );
};

export const notShowAllergiesQuestion = (ctx, action) => {
  return !(
    ctx.flags?.shortenedOnboardingQuiz ===
      ONBOARDING_VARIATIONS.requiredWithAllergies ??
    action?.payload.flags?.shortenedOnboardingQuiz ===
      ONBOARDING_VARIATIONS.requiredWithAllergies
  );
};

export const skipAllergiesQuestion = (ctx, action) =>
  !showAllergiesQuestion(ctx, action);
